/**
 * Splits a multi-selection property batch into one request per region.
 *
 * The deployed `AssetInfo/UpdateElementPropertyList` (stage/prod) writes the
 * elements into the single properties file named by the request's `RegionId`.
 * A plate shared by two regions (e.g. a longitudinal bulkhead) therefore has to
 * be sent once per region it belongs to — exactly what the single-element
 * `updateProperty` already does — otherwise it ends up in one region file only,
 * and not necessarily the loaded one (the first element's RegionIds order used
 * to decide). A backend that honours every id in `RegionIds` simply rewrites
 * the same values on the extra calls.
 */
export interface RegionBatch {
  regionId: string
  /** Property snapshots to send for this region; `RegionId` is set to the batch region, `RegionIds` is complete. */
  properties: any[]
}

export interface BatchElementLike {
  Guid?: string
  regionId?: string
  regionIds?: string[]
  properties?: any
}

const defaultClone = (properties: any): any => properties == null ? properties : JSON.parse(JSON.stringify(properties))

/** Every non-empty region id an element belongs to, in the element's own order (properties first, then the element fallbacks). */
export function elementRegionIds(element: BatchElementLike): string[] {
  const fromProperties: string[] | undefined = Array.isArray(element?.properties?.RegionIds) && element.properties.RegionIds.length > 0
    ? element.properties.RegionIds
    : undefined
  const ids: string[] = fromProperties ?? (element?.regionIds ?? (element?.regionId ? [element.regionId] : []))
  return Array.from(new Set<string>((ids ?? []).filter(regionId => !!regionId)))
}

export function buildRegionBatches(elements: BatchElementLike[], clone: (properties: any) => any = defaultClone): RegionBatch[] {
  const batches = new Map<string, RegionBatch>()
  for (const element of elements ?? []) {
    const regionIds = elementRegionIds(element)
    for (const regionId of regionIds) {
      const snapshot = clone(element.properties) ?? {}
      snapshot.RegionIds = [...regionIds]
      snapshot.RegionId = regionId
      let batch = batches.get(regionId)
      if (!batch) {
        batch = { regionId, properties: [] }
        batches.set(regionId, batch)
      }
      batch.properties.push(snapshot)
    }
  }
  return Array.from(batches.values())
}
